import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BLOG_POSTS, slugify } from '../data/blogContent';
import logoBlack from '../assets/images/logo-black.png';
import './BlogPage.css';
import './EnhancedMarketingLandingPage.css';

const BlogPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [seriesFilter, setSeriesFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const series = useMemo(() => Array.from(new Set(BLOG_POSTS.map(p => p.series))).sort(), []);
  const categories = useMemo(() => Array.from(new Set(BLOG_POSTS.map(p => p.category))).sort(), []);

  const filtered = useMemo(() => {
    return BLOG_POSTS.filter(p => {
      if (seriesFilter && p.series !== seriesFilter) return false;
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return p.title.toLowerCase().includes(q) ||
          p.hook.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q);
      }
      return true;
    });
  }, [search, seriesFilter, categoryFilter]);

  return (
    <>
      <Helmet>
        <title>Blog - BigSystemOne | Restaurant Management Insights</title>
        <meta name="description" content="Expert insights on multi-unit restaurant operations, guest retention, review management, and hospitality technology." />
      </Helmet>

      <div className="landing blog-page">
        {/* Header */}
        <header className="header header-scrolled">
          <div className="container">
            <div className="nav-brand">
              <Link to="/"><img src={logoBlack} alt="BigSystemOne" className="logo" /></Link>
            </div>
            <nav className="nav">
              <Link to="/#features">Features</Link>
              <Link to="/#how-it-works">How It Works</Link>
              <Link to="/#pricing">Pricing</Link>
              <Link to="/blog" style={{ fontWeight: 600 }}>Blog</Link>
              <Link to="/login" className="nav-cta">Access Platform</Link>
            </nav>
          </div>
        </header>

        {/* Hero */}
        <section className="blog-hero">
          <div className="container">
            <h1>BSO Blog</h1>
            <p>Insights and strategies for restaurant operators who want to grow smarter.</p>
          </div>
        </section>

        {/* Filters */}
        <div className="blog-filters">
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select value={seriesFilter} onChange={e => setSeriesFilter(e.target.value)}>
            <option value="">All Series</option>
            {series.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="blog-results-count">{filtered.length} post{filtered.length !== 1 ? 's' : ''}</div>
        </div>

        {/* Grid */}
        <div className="blog-grid">
          {filtered.map(post => {
            const slug = post.slug || slugify(post.title);
            return (
              <Link to={`/blog/${slug}`} className="blog-card" key={post.id}>
                <div className="blog-card-badges">
                  <span className="blog-badge blog-badge-series">{post.series}</span>
                  <span className="blog-badge blog-badge-category">{post.category}</span>
                </div>
                <h3>{post.title}</h3>
                <p className="blog-card-excerpt">
                  {post.hook.length > 150 ? post.hook.slice(0, 150) + '...' : post.hook}
                </p>
                <span className="blog-card-link">Read More &rarr;</span>
              </Link>
            );
          })}
        </div>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <img src={logoBlack} alt="BigSystemOne" className="footer-logo" />
                <p>Restaurant Reputation Management Platform</p>
              </div>
              <div className="footer-links">
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Service</Link>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 BigSystemOne. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPage;
